import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Phone, Mail, MapPin, Send, CheckCircle, Loader2, AlertCircle, MessageCircle, Building2, Layers,
  Box, Maximize2, ChevronLeft, ChevronRight, Home, ZoomIn, X
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import { useLanguage } from "../../context/LanguageContext";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

interface FlatData {
  no: number;
  flat: string;
  area: string;
  nos: string;
}

interface BlockData {
  name: string;
  title: string;
  description: string;
  flats: Array<{ id: number; name: string; image: string }>;
  tableData: FlatData[];
}

const HamdanComplex: React.FC<{ content?: any }> = ({ content }) => {
  const { t_api } = useLanguage();
  const services = content?.services || [
    " Extensive Residential Garden.",
    " Wide passages.",
    " Waiting lounges on each floor.",
    " Wide car park at four directions..",
    " Equipped with (12) elevators.",
    " Administrative services, guard and on-going maintenance."
  ];

  const layoutImages = [1, 2, 3, 4, 5, 6].map(i => content?.[`complex_layout_${i}`]).filter(Boolean);
  const displayLayoutImages = layoutImages.length > 0 ? layoutImages : [
    "/layout/layout1.jpg",
    "/layout/layout2.jpg",
    "/layout/layout3.jpg",
    "/layout/layout4.jpg",
    "/layout/layout5.jpg",
    "/layout/layout6.jpg",
  ];

  const [selectedBlock, setSelectedBlock] = useState<string>("A");
  const [selectedFlat, setSelectedFlat] = useState<{ name: string; image: string } | null>(null);

  const blockLabel = (blockKey: string) => {
    const arabicLabels: Record<string, string> = {
      A: "\u0627\u0644\u0645\u0628\u0646\u0649 \u0623",
      B: "\u0627\u0644\u0645\u0628\u0646\u0649 \u0628",
      C: "\u0627\u0644\u0645\u0628\u0646\u0649 \u062c",
    };

    return t_api(`Block ${blockKey}`, arabicLabels[blockKey] || `Block ${blockKey}`);
  };

  const blocksData: Record<string, BlockData> = {
    A: {
      name: "Block A",
      title: content?.complex_block_A_title || "Block A consists of two symmetrical buildings:",
      description: content?.complex_block_A_desc ||
        "Flats are of diverse design and fully furnished with distinct artistic touching. Inclusive of all general services, such as toilets and kitchens. The floors of the building are divided into multiple models to satisfy lodgers’ needs, services and convenience.",
      flats: [1, 2, 3, 4].map(i => ({
        id: i,
        name: `Flat ${i}`,
        image: content?.[`complex_block_A_flat_${i}`] || `/floor/blockA_${i}.jpg`
      })).filter(f => f.image),
      tableData: [
        { no: 1, flat: "A1a", area: "Area", nos: "Nos." },
        { no: 2, flat: "A1b", area: "Area", nos: "Nos." },
        { no: 3, flat: "A1c", area: "Area", nos: "Nos." },
      ],
    },
    B: {
      name: "Block B",
      title: content?.complex_block_B_title || "Block B consists of two symmetrical buildings:",
      description: content?.complex_block_B_desc || "Flats are of diverse design and fully furnished with distinct artistic touching. Inclusive of all general services, such as toilets and kitchens. The floors of the building are divided into multiple models to satisfy lodgers’ needs, services and convenience.",
      flats: [1, 2, 3, 4].map(i => ({
        id: i,
        name: `Flat ${i}`,
        image: content?.[`complex_block_B_flat_${i}`] || `/floor/BlockB_${i}.jpg`
      })).filter(f => f.image),
      tableData: [
        { no: 1, flat: "B1a", area: "Area", nos: "Nos." },
        { no: 2, flat: "B1b", area: "Area", nos: "Nos." },
        { no: 3, flat: "B1c", area: "Area", nos: "Nos." },
      ],
    },
    C: {
      name: "Block C",
      title: content?.complex_block_C_title || "Block C consists of two symmetrical buildings:",
      description: content?.complex_block_C_desc || "Flats are of diverse design and fully furnished with distinct artistic touching. Inclusive of all general services, such as toilets and kitchens. The floors of the building are divided into multiple models to satisfy lodgers’ needs, services and convenience.",
      flats: [1, 2, 3, 4].map(i => ({
        id: i,
        name: `Flat ${i}`,
        image: content?.[`complex_block_C_flat_${i}`] || `/floor/BlockC_${i}.jpg`
      })).filter(f => f.image),
      tableData: [
        { no: 1, flat: "C1a", area: "Area", nos: "Nos." },
        { no: 2, flat: "C1b", area: "Area", nos: "Nos." },
        { no: 3, flat: "C1c", area: "Area", nos: "Nos." },
      ],
    },
  };

  const currentBlock = blocksData[selectedBlock];

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden border">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-10 items-center p-4 sm:p-8 md:p-10">

            {/* Left Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={content?.image || "/Hamdan-Residential Complex.jpg"}
                alt="Hamdan Residential Complex"
                className="rounded-xl sm:rounded-2xl shadow-lg object-cover w-full h-[250px] sm:h-[350px] md:h-[450px]"
              />
            </motion.div>

            {/* Right Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                {content?.title || "Hamdan Residential Complex"}
              </h2>

              <div className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base text-justify mb-3 sm:mb-4" dangerouslySetInnerHTML={{ __html: (content?.desc1 || "Hamdan Residential Complex is considered to be one the biggest residential projects based in Dhofar Governorate. It enjoys a strategic location in the middle of Salalah as it is located in the vicinity of Salalah Gardens Mall, Lulu Hypermarket and Salalah Airport. In addition, it maintains magnificent overview on Salalah hills and Entertaining Municipality Center. The complex also in its unique design consists of a splendid and wide range of distinct designs of residential flats.").replace(/\n/g, '<br/>') }} />
              <div className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base text-justify mb-3 sm:mb-4" dangerouslySetInnerHTML={{ __html: (content?.desc2 || "Spaces between residential buildings have been provided when designing the complex in consideration of privacy, ventilation and movement. The design of the complex in its entirety, colors and finishing materials reflects the spirit of architecture that is in line with the rest of other elements components of the project.").replace(/\n/g, '<br/>') }} />
              <p className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base text-justify mb-3 sm:mb-4">
                The convenience of the lodgers is highly considered as the premises being directed towards yards, spaces, internal attractive green landscapes, in addition to the graded arcades that add a mechanical and more beautiful rhythm at the same time.
              </p>
            </motion.div>
          </div>


          {/* Services Section */}
          <div className="px-4 sm:px-6 md:px-10 pt-4 sm:pt-6 pb-4 sm:pb-5">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4"
            >
              Complex Services
            </motion.h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              {services.map((service: any, i: number) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start p-3 sm:p-4 bg-gray-50 rounded-lg shadow hover:shadow-md transition"
                >
                  <span className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full bg-red-100 text-red-600 font-semibold mr-2 sm:mr-3 text-xs sm:text-sm flex-shrink-0">
                    {i + 1}
                  </span>
                  <p className="text-gray-700 text-xs sm:text-sm flex-1">{service}</p>

                </motion.div>
              ))}
            </div>
          </div>

          {/* Advanced Visualization Section */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto mt-6 sm:mt-10 px-4 sm:px-6 lg:px-12"
          >
            {/* Section Title */}
            <div className="text-center mb-6 sm:mb-10">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 px-2">
                Complex General Layout -3D Garden Perspective
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {/* Left Features */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="space-y-3 sm:space-y-5"
              >
                {[
                  { icon: Box, title: "3D Modeling", desc: "Full dimensional visualization" },
                  { icon: Maximize2, title: "Scalable Views", desc: "Zoom and explore details" },
                  { icon: Layers, title: "Layer Control", desc: "Toggle elements on/off" },
                ].map((feature, idx) => (
                  <motion.div
                    key={idx}
                    whileHover={{ scale: 1.03 }}
                    className="bg-white rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-all border border-gray-100 flex flex-col items-center text-center"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-red-100 text-red-600 mb-2 sm:mb-3">
                      <feature.icon className="w-4 h-4 sm:w-6 sm:h-6" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-xs sm:text-sm md:text-base mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 leading-snug">
                      {feature.desc}
                    </p>
                  </motion.div>
                ))}
              </motion.div>

              {/* Right Carousel */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="md:col-span-2 w-full"
              >
                <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5 shadow-xl border border-gray-200">
                  <Swiper
                    modules={[Autoplay, Pagination]}
                    autoplay={{ delay: 3000, disableOnInteraction: false }}
                    pagination={{ clickable: true, el: ".custom-pagination" }}
                    loop
                    slidesPerView={1}
                    className="rounded-xl overflow-hidden"
                  >
                    {displayLayoutImages.map((src: string, i: number) => (
                      <SwiperSlide key={i}>
                        <div className="relative bg-gray-50 rounded-xl w-full overflow-hidden">
                          <img
                            src={src}
                            alt={`Complex View ${i + 1}`}
                            className="w-full h-64 sm:h-72 md:h-96 object-contain"
                            onError={(e) => (e.currentTarget.src = "/fallback.jpg")}
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  {/* Pagination */}
                  <div className="mt-3 sm:mt-5 w-full text-center">
                    <div className="custom-pagination inline-flex items-center gap-2"></div>
                  </div>
                  {/* Pagination Style */}
                  <style>{`
                        .custom-pagination .swiper-pagination-bullet {
                          background: rgb(203, 213, 225);
                          opacity: 1;
                          width: 8px;
                          height: 8px;
                          transition: all 0.3s;
                          cursor: pointer;
                        }
                        .custom-pagination .swiper-pagination-bullet-active {
                          background: rgba(174, 4, 24, 1);
                          width: 20px;
                          border-radius: 5px;
                        }
                      `}</style>
                </div>
              </motion.div>
            </div>
          </motion.section>


          {/* Block  */}
          <div className="bg-white">

            {/* Header */}
            <div className="mt-8 sm:mt-12 sticky top-0 z-40 bg-white shadow-sm" data-no-auto-translate="true">
              <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Title Section */}
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg shrink-0">
                      <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                    </div>
                    <div className="min-w-0">
                      <h1 className="text-lg sm:text-2xl font-bold text-stone-900 leading-tight">
                        {t_api("Hamdan Complex", "\u0645\u062c\u0645\u0639 \u062d\u0645\u062f\u0627\u0646")}
                      </h1>
                      <p className="text-xs sm:text-sm text-stone-500 leading-snug">
                        {t_api("Residential Floor Plans", "\u0645\u062e\u0637\u0637\u0627\u062a \u0627\u0644\u0637\u0648\u0627\u0628\u0642 \u0627\u0644\u0633\u0643\u0646\u064a\u0629")}
                      </p>
                    </div>
                  </div>

                  {/* Block Selector */}
                  <div className="flex flex-wrap gap-2 sm:justify-end">
                    {Object.keys(blocksData).map((blockKey) => (
                      <button
                        key={blockKey}
                        type="button"
                        onClick={() => setSelectedBlock(blockKey)}
                        className={`min-w-[88px] px-4 sm:px-5 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${selectedBlock === blockKey
                          ? "bg-red-600 text-white shadow-md"
                          : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-100"
                          }`}
                      >
                        {blockLabel(blockKey)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Block Info Section */}
            <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-6 sm:pt-10 pb-6 sm:pb-8">
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-stone-200 p-4 sm:p-8 mb-6 sm:mb-8">
                <div className="flex gap-3 sm:gap-5">
                  <div className="p-2 sm:p-3 bg-red-100 rounded-lg h-fit">
                    <Layers className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-2xl font-bold text-stone-900">{blockLabel(selectedBlock)}</h2>
                    <p className="text-stone-600 mt-1 text-xs sm:text-base">{currentBlock.title}</p>
                    <p className="text-stone-500 mt-2 text-xs sm:text-sm text-justify">{currentBlock.description}</p>
                  </div>
                </div>
              </div>



              {/* Floor Plans */}
              <h3 className="text-base sm:text-xl font-bold text-stone-800 mb-4 sm:mb-6 flex items-center gap-2">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                Available Floor Plans
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-12">
                {currentBlock.flats.map((flat) => (
                  <div
                    key={flat.id}
                    onClick={() => setSelectedFlat(flat)}
                    className="bg-white border border-stone-200 rounded-lg sm:rounded-xl shadow-sm hover:shadow-lg transition cursor-pointer overflow-hidden group"
                  >
                    <div className="relative h-28 sm:h-40 bg-stone-100 flex items-center justify-center">
                      <img
                        src={flat.image}
                        alt={flat.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => (e.currentTarget.src = "/fallback.jpg")}
                      />

                    </div>
                    <div className="p-2 sm:p-4">
                      <p className="text-stone-700 font-medium text-xs sm:text-base">Floor Plan {flat.id}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Specifications Table */}
              <div className="bg-white rounded-xl sm:rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
                <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-stone-200 flex items-center gap-2">
                  <Layers className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                  <h4 className="font-bold text-stone-800 text-sm sm:text-base">{currentBlock.name} Specifications</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-stone-100">
                        <th className="px-3 sm:px-6 py-2 sm:py-3 font-semibold text-stone-700">No.</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 font-semibold text-stone-700">Flat</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 font-semibold text-stone-700">Area</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 font-semibold text-stone-700">Nos.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentBlock.tableData.map((row, i) => (
                        <tr key={i} className="hover:bg-red-50 transition">
                          <td className="px-3 sm:px-6 py-2 sm:py-3">{row.no}</td>
                          <td className="px-3 sm:px-6 py-2 sm:py-3 font-medium text-red-600">{row.flat}</td>
                          <td className="px-3 sm:px-6 py-2 sm:py-3 text-stone-600">{row.area}</td>
                          <td className="px-3 sm:px-6 py-2 sm:py-3 text-stone-600">{row.nos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal */}
            {selectedFlat && (
              <div
                className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
                onClick={() => setSelectedFlat(null)}
              >
                <div
                  className="bg-white rounded-xl shadow-2xl max-w-4xl w-full p-4 sm:p-6 relative max-h-[90vh] overflow-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setSelectedFlat(null)}
                    className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-stone-100 hover:bg-stone-200 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center z-10"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                  <h3 className="text-lg sm:text-2xl font-bold text-stone-900 mb-3 sm:mb-4 pr-10">
                    {currentBlock.name} - {selectedFlat.name}
                  </h3>
                  <div className="h-[300px] sm:h-[500px] bg-stone-100 rounded-lg flex items-center justify-center">
                    <img
                      src={selectedFlat.image}
                      alt={selectedFlat.name}
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => (e.currentTarget.src = "/fallback.jpg")}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>

  );
};

export default HamdanComplex;
